/**
 * Rekonsiliasi Kas Staf — satu sumber perhitungan 9 metrik akuntabilitas.
 *
 * READ-ONLY. Satu sumber: GET /petty-cash/accountability-report/{user_id}
 * + GET /petty-cash/accountability-overview (BOD/FAT).
 *
 * Metrik, label kelengkapan, dan rincian transaksi identik dengan yang
 * ditampilkan Dashboard (Rekap Saldo Staff) dan Kas & Bank (Ledger staf).
 */
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { ArrowLeft, Info, RefreshCw, Wallet, AlertTriangle, Users, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import SearchableSelect from "@/components/SearchableSelect";
import DrillDownDrawer from "@/components/petty-cash/DrillDownDrawer";

const COMPLETENESS_META = {
  data_lengkap: { label: "Data Lengkap", cls: "text-emerald-800 bg-emerald-100 border-emerald-300" },
  sebagian_estimasi: { label: "Sebagian Estimasi", cls: "text-amber-800 bg-amber-100 border-amber-300" },
  estimasi: { label: "Estimasi", cls: "text-amber-800 bg-amber-100 border-amber-300" },
  belum_dapat_dipastikan: { label: "Belum Dapat Dipastikan", cls: "text-rose-800 bg-rose-100 border-rose-300" },
  tidak_ada_data: { label: "Tidak Ada Data", cls: "text-slate-600 bg-slate-100 border-slate-300" },
  tidak_terpetakan_ke_proyek: { label: "Tidak Terpetakan", cls: "text-slate-600 bg-slate-100 border-slate-300" },
};

function MetricCard({ metric, defs, testid, onDrill }) {
  const d = defs?.[metric.key] || {};
  const m = metric.data || {};
  const comp = m.completeness || "data_lengkap";
  const meta = COMPLETENESS_META[comp] || COMPLETENESS_META.data_lengkap;
  const title = [
    d.label || metric.key,
    d.formula ? `\nRumus: ${d.formula}` : "",
    d.source ? `\nSumber: ${d.source}` : "",
    d.catatan ? `\nCatatan: ${d.catatan}` : "",
  ].join("");
  const valDisplay = m.value === null
    ? "— (belum dapat dipastikan)"
    : formatIDR(m.value ?? 0);
  return (
    <div
      className={`border bg-white p-3 border-slate-200 flex flex-col ${onDrill ? "cursor-pointer hover:border-[#FF5A1F] transition-colors" : ""}`}
      data-testid={testid}
      title={title}
      onClick={onDrill}
      role={onDrill ? "button" : undefined}
      tabIndex={onDrill ? 0 : undefined}
      onKeyDown={onDrill ? (e) => { if (e.key === "Enter" || e.key === " ") onDrill(); } : undefined}
    >
      <div className="flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-slate-500 truncate flex-1">
          {d.label || metric.key}
        </span>
        <Info size={10} className="text-slate-300 shrink-0" />
      </div>
      <div className={`font-display text-lg tabular-nums mt-1 ${m.value === null || (typeof m.value === "number" && m.value < 0) ? "text-rose-600" : "text-[#0B1D3A]"}`}>
        {valDisplay}
      </div>
      <span
        className={`inline-block mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border w-fit ${meta.cls}`}
        data-testid={`${testid}-badge`}
      >
        {meta.label}
      </span>
      {m.note && <div className="text-[10px] text-slate-500 mt-1 italic">{m.note}</div>}
      {Array.isArray(m.reasons) && m.reasons.length > 0 && (
        <ul className="text-[10px] text-rose-700 mt-1 list-disc pl-3">
          {m.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {metric.key === "saldo_pertanggungjawaban" && m.value === null && (
        <div className="text-[10px] text-slate-500 mt-1">
          <div>Strict (tanpa legacy): <b>{formatIDR(m.value_strict_tanpa_legacy ?? 0)}</b></div>
          <div>Termasuk estimasi: <b>{formatIDR(m.value_termasuk_estimasi_legacy ?? 0)}</b></div>
        </div>
      )}
      {metric.key === "saldo_tersedia" && (
        <div className="text-[10px] text-slate-500 mt-1">
          Estimasi (jika cadangan 0): <b>{formatIDR(m.estimasi_jika_cadangan_nol ?? 0)}</b>
          <div className="italic">Bukan angka siap pakai.</div>
        </div>
      )}
      {onDrill && (
        <div className="text-[9px] text-slate-400 mt-auto pt-1 italic" data-testid={`${testid}-drill-hint`}>
          Klik untuk rincian pembentuk
        </div>
      )}
    </div>
  );
}

export default function RekonsiliasiKasStafPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canSelectOthers = canPerm(user, "petty_cash", "approve");
  const canBackfillLegacy = canPerm(user, "petty_cash", "confirm_transfer");
  const canVerifyItem = canPerm(user, "petty_cash", "verify_actual");

  const [targetUserId, setTargetUserId] = useState(user?.id || "");
  const [asOf, setAsOf] = useState("");
  const [projectId, setProjectId] = useState("");
  const [tab, setTab] = useState("metrics"); // metrics | overview | transactions
  const [report, setReport] = useState(null);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [legacyDialog, setLegacyDialog] = useState(null); // {pc_id, week_label, project_name, fallback_total}
  const [drill, setDrill] = useState(null); // {category} | {title, rows, defaultStatus}

  // Drill-down: filter daftar mengikuti kartu yang diklik (read-only).
  const drillForMetric = useCallback((key) => {
    if (!report) return null;
    const tx = report.transactions || {};
    const dd = report.drilldown || {};
    const firstNonEmptyCat = ["status_tidak_selaras", "konflik_nominal", "menunggu_verifikasi", "dokumen_belum_terverifikasi"]
      .find((c) => (dd[c]?.count || 0) > 0) || "menunggu_verifikasi";
    switch (key) {
      case "dana_diterima_terkonfirmasi":
        return { title: "Dana Diterima Terkonfirmasi", rows: (tx.transfers || []).filter((r) => r.kind !== "legacy_estimated"), defaultStatus: "Transfer terkonfirmasi" };
      case "dana_diterima_estimasi_legacy":
        return { title: "Estimasi Dana Historis", rows: (tx.transfers || []).filter((r) => r.kind === "legacy_estimated"), defaultStatus: "Estimasi dana historis" };
      case "realisasi_verified":
        return { title: "Realisasi Terverifikasi", rows: tx.realisasi_verified || [], defaultStatus: "Terverifikasi" };
      case "realisasi_pending":
        return { category: "menunggu_verifikasi" };
      case "realisasi_ditolak_butuh_tindak_lanjut":
        return { title: "Realisasi Ditolak (Butuh Tindak Lanjut)", rows: tx.realisasi_ditolak || [], defaultStatus: "Ditolak" };
      case "realisasi_konflik_status":
        return { category: "status_tidak_selaras" };
      case "pengembalian_verified":
        return { title: "Pengembalian Kas Terverifikasi", rows: (tx.returns || []).filter((r) => r.status === "verified"), defaultStatus: "Terverifikasi" };
      case "pengembalian_pending":
        return { title: "Pengembalian Kas Menunggu Verifikasi", rows: (tx.returns || []).filter((r) => r.status === "pending"), defaultStatus: "Menunggu Verifikasi" };
      case "dana_belum_dilaporkan_penggunaannya":
        return { title: "Dana Belum Dilaporkan Penggunaannya", rows: tx.dana_belum_dilaporkan || [], defaultStatus: "Belum dilaporkan" };
      case "cadangan_aktif_belum_digunakan":
        return { title: "Cadangan Aktif Belum Digunakan", rows: tx.cadangan_aktif || [], defaultStatus: "Cadangan aktif" };
      default: // saldo_pertanggungjawaban, estimasi_saldo, saldo_tersedia
        return { category: firstNonEmptyCat };
    }
  }, [report]);

  // Load users list (only if can view others)
  useEffect(() => {
    if (!canSelectOthers) return;
    api.get("/petty-cash/submitters").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [canSelectOthers]);

  const loadReport = useCallback(() => {
    if (!targetUserId) return;
    setLoading(true); setErr(null);
    const params = new URLSearchParams();
    if (asOf) params.set("as_of", asOf);
    if (projectId) params.set("project_id", projectId);
    api.get(`/petty-cash/accountability-report/${targetUserId}${params.toString() ? `?${params}` : ""}`)
      .then((r) => setReport(r.data))
      .catch((e) => {
        setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat laporan");
        setReport(null);
        if (e.response?.status === 403) toast.error("Akses ditolak.");
      })
      .finally(() => setLoading(false));
  }, [targetUserId, asOf, projectId]);

  const loadOverview = useCallback(() => {
    if (!canSelectOthers) return;
    const q = asOf ? `?as_of=${asOf}` : "";
    api.get(`/petty-cash/accountability-overview${q}`)
      .then((r) => setOverview(r.data))
      .catch(() => setOverview(null));
  }, [canSelectOthers, asOf]);

  useEffect(() => { if (tab === "metrics" || tab === "transactions") loadReport(); }, [loadReport, tab]);
  useEffect(() => { if (tab === "overview") loadOverview(); }, [loadOverview, tab]);

  // Verifikasi 1 langsung dari drawer (alur resmi → Audit Log). Read-only tetap
  // berlaku untuk user tanpa perm verify_actual (tombol tidak ditampilkan).
  const verifyItemFromDrawer = useCallback(async (pcId, itemId) => {
    try {
      await api.post(`/petty-cash/${pcId}/verify-item`, { item_id: itemId, action: "verify", note: null });
      toast.success("Item lolos Verifikasi 1 (pengeluaran)");
      loadReport();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memverifikasi item");
      throw e;
    }
  }, [loadReport]);

  // Mode massal: verifikasi berurutan per item via endpoint resmi — setiap item
  // tetap tercatat sendiri di Audit Log (dengan alasan wajib); reload sekali di akhir.
  const verifyItemsBulk = useCallback(async (items, reason) => {
    let ok = 0;
    let fail = 0;
    for (const r of items) {
      try {
        await api.post(`/petty-cash/${r.pc_id}/verify-item`, { item_id: r.item_id, action: "verify", note: reason || null });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(formatDetail(e.response?.data?.detail) || "Gagal memverifikasi item");
      }
    }
    if (ok > 0) toast.success(`${ok} item lolos Verifikasi 1 (pengeluaran)`);
    loadReport();
    return { ok, fail };
  }, [loadReport]);

  const metricsConfig = useMemo(() => ([
    { key: "dana_diterima_terkonfirmasi", testid: "mk-dana-terkonfirmasi" },
    { key: "dana_diterima_estimasi_legacy", testid: "mk-dana-legacy" },
    { key: "realisasi_verified", testid: "mk-realisasi-verified" },
    { key: "realisasi_pending", testid: "mk-realisasi-pending" },
    { key: "realisasi_ditolak_butuh_tindak_lanjut", testid: "mk-realisasi-ditolak" },
    { key: "realisasi_konflik_status", testid: "mk-realisasi-konflik" },
    { key: "pengembalian_verified", testid: "mk-return-verified" },
    { key: "pengembalian_pending", testid: "mk-return-pending" },
    { key: "saldo_pertanggungjawaban", testid: "mk-saldo-pj" },
    { key: "estimasi_saldo", testid: "mk-estimasi-saldo" },
    { key: "dana_belum_dilaporkan_penggunaannya", testid: "mk-belum-dilaporkan" },
    { key: "cadangan_aktif_belum_digunakan", testid: "mk-cadangan" },
    { key: "saldo_tersedia", testid: "mk-saldo-tersedia" },
  ]), []);

  const avail = report?.available_projects || [];
  const projectOptions = useMemo(() => ([
    { value: "", label: "Semua proyek (Global)" },
    ...avail.map((p) => ({
      value: p.project_id || "__unmapped__",
      label: p.project_name || "(Belum terpetakan)",
    })),
  ]), [avail]);

  const userOptions = useMemo(() => (
    users.map((u) => ({ value: u.user_id, label: `${u.user_name} · ${u.division}` }))
  ), [users]);

  return (
    <div className="p-6 flex flex-col gap-4" data-testid="pc-recon-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => nav(-1)}
          className="p-1.5 border border-slate-200 hover:bg-slate-50"
          data-testid="pc-recon-back"
        ><ArrowLeft size={14} /></button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="font-display text-xl tracking-tight text-[#0B1D3A] flex items-center gap-2">
            <Wallet size={18} /> Rekonsiliasi Kas Staf
          </h1>
          <p className="text-[11px] text-slate-500">
            Rincian perhitungan saldo petty cash staf dan transaksi pembentuknya · Read-only · tidak mengubah transaksi
          </p>
        </div>
        <button
          onClick={tab === "overview" ? loadOverview : loadReport}
          className="px-2.5 py-1.5 border border-slate-200 text-[11px] hover:bg-slate-50 inline-flex items-center gap-1.5"
          data-testid="pc-recon-refresh"
        >
          <RefreshCw size={12} /> Muat Ulang
        </button>
      </div>

      {/* Catatan halaman — ringkas, netral, tanpa label fase */}
      <div className="border border-slate-200 bg-slate-50 p-3 flex items-start gap-2 text-[11px] text-slate-700" data-testid="pc-recon-fase-notice">
        <Info size={14} className="shrink-0 mt-0.5" />
        <div>
          Halaman ini merinci perhitungan saldo petty cash staf: <b>Dana Diterima
          Terkonfirmasi</b>, <b>Estimasi Dana Historis</b>, <b>Realisasi Terverifikasi</b>,
          <b> Realisasi Menunggu Verifikasi 1</b>, <b>Realisasi Ditolak</b>, <b>Pengembalian
          Kas Terverifikasi / Menunggu Verifikasi</b>, <b>Saldo Pertanggungjawaban</b>,
          <b>Estimasi Saldo</b>, dan <b>Saldo Tersedia untuk Alokasi</b>.
          Verifikasi 1 adalah verifikasi pengeluaran (menentukan status terverifikasi
          secara finansial); Verifikasi 2 adalah penentuan COA setelah Verifikasi 1 —
          tidak mengubah nominal, saldo, atau cadangan. Saldo negatif tampil apa adanya.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { k: "metrics", label: "Metrik" },
          { k: "transactions", label: "Transaksi Pembentuk" },
          ...(canSelectOthers ? [{ k: "overview", label: "Ringkasan Semua Staf" }] : []),
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`pc-recon-tab-${t.k}`}
            className={`px-3 py-1.5 text-[11px] border-b-2 ${
              tab === t.k ? "border-[#FF5A1F] text-[#0B1D3A] font-semibold" : "border-transparent text-slate-500"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* Filters */}
      {tab !== "overview" && (
        <div className="flex flex-wrap gap-3 items-end">
          {canSelectOthers && (
            <div className="min-w-[260px]">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                <Users size={10} /> Staf
              </label>
              <SearchableSelect
                value={targetUserId}
                onChange={setTargetUserId}
                options={[{ value: user?.id || "", label: `${user?.name || "Diri Sendiri"} (Anda)` }, ...userOptions.filter((o) => o.value !== user?.id)]}
                placeholder="Pilih staf…"
                testId="pc-recon-user-picker"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Tanggal acuan</label>
            <input
              type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
              className="border border-slate-300 px-2 py-1 text-xs"
              data-testid="pc-recon-asof"
            />
          </div>
          <div className="min-w-[220px]">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Proyek</label>
            <SearchableSelect
              value={projectId}
              onChange={setProjectId}
              options={projectOptions}
              placeholder="Semua proyek"
              testId="pc-recon-project-picker"
            />
          </div>
        </div>
      )}

      {/* Error */}
      {err && <div className="border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" data-testid="pc-recon-err">{err}</div>}

      {/* Warnings */}
      {report?.warnings?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900" data-testid="pc-recon-warnings">
          <div className="font-semibold mb-1">Catatan Ketidakpastian:</div>
          <ul className="list-disc pl-4">
            {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Metrics tab */}
      {tab === "metrics" && (
        <>
          {loading && <div className="text-xs text-slate-500" data-testid="pc-recon-loading">Memuat…</div>}
          {!loading && report && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="pc-recon-metrics-grid">
              {metricsConfig.map((mc) => (
                <MetricCard
                  key={mc.key}
                  metric={{ key: mc.key, data: report.metrics?.[mc.key] }}
                  defs={report.definitions}
                  testid={mc.testid}
                  onDrill={() => setDrill(drillForMetric(mc.key))}
                />
              ))}
            </div>
          )}
          {!loading && report && (
            <div className="border border-slate-200 bg-slate-50 p-3 text-[10px] text-slate-600">
              <b>Scope:</b> {report.scope?.is_global ? "Global (semua proyek)" :
                report.scope?.is_unmapped_bucket ? "Belum Terpetakan / Office" :
                `Proyek ID: ${report.scope?.project_id}`}
              {" · "}<b>Tanggal acuan:</b> {report.as_of}
              {report.is_historical_snapshot && <span className="ml-1 text-amber-700">(snapshot historis)</span>}
            </div>
          )}
        </>
      )}

      {/* Transactions tab */}
      {tab === "transactions" && report && (
        <div className="space-y-4" data-testid="pc-recon-trx">
          <LegacyBackfillSection
            rows={(report.transactions?.transfers || []).filter((r) => r.kind === "legacy_estimated")}
            canBackfill={canBackfillLegacy}
            onOpen={(row) => setLegacyDialog(row)}
          />
          <TrxTable
            title="Transfer Dana Diterima"
            testid="trx-transfers"
            rows={report.transactions?.transfers || []}
            cols={[
              { k: "week_label", label: "Minggu" },
              { k: "project_name", label: "Proyek" },
              { k: "transfer_date", label: "Tgl Transfer" },
              { k: "transfer_amount", label: "Nominal", money: true, allowNull: "Legacy (fallback total)" },
              { k: "fallback_total", label: "Fallback Total", money: true },
              { k: "kind", label: "Tipe" },
            ]}
          />
          <TrxTable
            title="Realisasi Terverifikasi"
            testid="trx-verified"
            rows={report.transactions?.realisasi_verified || []}
            cols={[
              { k: "week_label", label: "Minggu" },
              { k: "project_name", label: "Proyek" },
              { k: "verified_at", label: "Verified At" },
              { k: "actual_amount", label: "Nominal", money: true },
            ]}
          />
          <TrxTable
            title="Realisasi Menunggu Verifikasi 1"
            testid="trx-pending"
            rows={report.transactions?.realisasi_pending || []}
            cols={[
              { k: "name", label: "Item" },
              { k: "project_name", label: "Proyek" },
              { k: "filled_at", label: "Diisi" },
              { k: "actual_amount", label: "Nominal", money: true },
            ]}
          />
          <TrxTable
            title="Realisasi Ditolak (Butuh Tindak Lanjut)"
            testid="trx-rejected"
            rows={report.transactions?.realisasi_ditolak || []}
            cols={[
              { k: "name", label: "Item" },
              { k: "project_name", label: "Proyek" },
              { k: "actual_amount", label: "Nominal", money: true },
              { k: "reject_note", label: "Alasan Tolak" },
            ]}
          />
          <TrxTable
            title="Pengembalian Kas (Tidak Terikat Proyek)"
            testid="trx-returns"
            rows={report.transactions?.returns || []}
            cols={[
              { k: "return_date", label: "Tgl" },
              { k: "status", label: "Status" },
              { k: "amount", label: "Nominal", money: true },
              { k: "project_mapping", label: "Pemetaan Proyek" },
            ]}
          />
          <TrxTable
            title="Dana Belum Dilaporkan Penggunaannya (Bukan Alokasi Aktif)"
            testid="trx-belum-dilaporkan"
            rows={report.transactions?.dana_belum_dilaporkan || []}
            cols={[
              { k: "week_label", label: "Minggu" },
              { k: "project_name", label: "Proyek" },
              { k: "transferred_at", label: "Transfer At" },
              { k: "amount", label: "Nominal", money: true },
              { k: "kind", label: "Tipe" },
            ]}
          />
          <CadanganBreakdownTable rows={report.transactions?.cadangan_aktif || []}
            metric={report.metrics?.cadangan_aktif_belum_digunakan} />
          {(report.rekonsiliasi_perlu_ditinjau || []).length > 0 && (
            <KonflikTable
              rows={report.rekonsiliasi_perlu_ditinjau}
              userName={report.user?.name || report.scope?.user_name}
              onDrill={() => setDrill({ category: "status_tidak_selaras" })}
            />
          )}
        </div>
      )}

      {/* Drill-down drawer */}
      {drill && (
        <DrillDownDrawer
          drill={drill}
          report={report}
          canVerify={canVerifyItem}
          onVerifyItem={verifyItemFromDrawer}
          onVerifyItems={verifyItemsBulk}
          onClose={() => setDrill(null)}
        />
      )}

      {/* Legacy backfill dialog */}
      {legacyDialog && (
        <LegacyBackfillDialog
          row={legacyDialog}
          onClose={() => setLegacyDialog(null)}
          onDone={() => { setLegacyDialog(null); loadReport(); }}
        />
      )}

      {/* Overview tab */}
      {tab === "overview" && (
        <div data-testid="pc-recon-overview-tab">
          {!overview && <div className="text-xs text-slate-500">Memuat…</div>}
          {overview && (
            <div className="border border-slate-200 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {["Staf", "Divisi", "Dana Diterima Terkonfirmasi", "Estimasi Dana Historis", "Realisasi Terverifikasi",
                      "Realisasi Menunggu Verifikasi 1", "Realisasi Ditolak", "Pengembalian Kas Terverifikasi", "Pengembalian Kas Menunggu Verifikasi",
                      "Saldo Pertanggungjawaban", "Estimasi Saldo", "Belum Dilaporkan", "Saldo Tersedia untuk Alokasi"].map((h) => (
                      <th key={h} className="text-left px-2 py-1.5 border-b border-slate-200 sticky top-0 bg-slate-50">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(overview.rows || []).map((r) => (
                    <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5">
                        <button
                          className="text-[#0B1D3A] hover:underline text-left"
                          onClick={() => { setTargetUserId(r.user_id); setTab("metrics"); }}
                        >{r.user_name}</button>
                        {r.has_legacy && <span className="ml-1 text-[9px] text-amber-700">(legacy)</span>}
                      </td>
                      <td className="px-2 py-1.5">{r.division}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.dana_diterima_terkonfirmasi)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{formatIDR(r.dana_diterima_estimasi_legacy)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.realisasi_verified)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.realisasi_pending)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{formatIDR(r.realisasi_ditolak)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.pengembalian_verified)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.pengembalian_pending)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                        {r.saldo_pertanggungjawaban === null
                          ? <span className="text-rose-700">Belum dapat dipastikan</span>
                          : formatIDR(r.saldo_pertanggungjawaban)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{formatIDR(r.estimasi_saldo)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIDR(r.dana_belum_dilaporkan)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">
                        {r.saldo_tersedia === null ? "Belum dapat dipastikan" : formatIDR(r.saldo_tersedia)}
                      </td>
                    </tr>
                  ))}
                  {(overview.rows || []).length === 0 && (
                    <tr><td colSpan={13} className="text-center p-4 text-slate-500">Tidak ada data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KonflikTable({ rows, userName, onDrill }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-amber-300 bg-amber-50" data-testid="pc-recon-perlu-tinjau">
      <div className="px-3 py-2 text-[11px] font-semibold text-amber-900 border-b border-amber-200 flex items-center gap-1.5 flex-wrap">
        <AlertTriangle size={12} />
        Perlu Tinjauan Berwenang —
        {onDrill ? (
          <button
            onClick={onDrill}
            className="underline decoration-dotted hover:text-[#FF5A1F]"
            data-testid="konflik-count-drill"
            title="Klik untuk membuka drawer rincian konflik"
          >{rows.length} item</button>
        ) : (
          <span>{rows.length} item</span>
        )}
        <span>dengan status verifikasi belum selaras</span>
        <span className="font-normal text-amber-700">
          · Melihat daftar ini tidak mengubah status apa pun; penyelesaian hanya melalui keputusan berwenang.
        </span>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-amber-100/60">
            <tr>
              {["Staf", "Proyek", "Periode", "Nama Item", "Nominal Realisasi", "Status Dokumen", "Status Item", "Alasan", "Pengajuan"].map((h) => (
                <th key={h} className="text-left px-2 py-1 border-b border-amber-200 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={`${x.pc_id}-${x.item_id || i}`} className="border-b border-amber-100" data-testid={`konflik-row-${i}`}>
                <td className="px-2 py-1">{userName || "Tidak tersedia"}</td>
                <td className="px-2 py-1">{x.project_name || "Tidak tersedia"}</td>
                <td className="px-2 py-1 whitespace-nowrap">{x.week_label || "Tidak tersedia"}</td>
                <td className="px-2 py-1">{x.item_name || "Tidak tersedia"}</td>
                <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                  {x.actual_amount != null ? formatIDR(x.actual_amount) : "Tidak tersedia"}
                </td>
                <td className="px-2 py-1">{x.doc_status || "Terverifikasi"}</td>
                <td className="px-2 py-1">{x.item_status || "Tidak tersedia"}</td>
                <td className="px-2 py-1">
                  <div>{x.alasan || "Tidak tersedia"}</div>
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-[9px] uppercase tracking-wider text-slate-500 hover:text-[#0B1D3A]" data-testid={`konflik-detail-teknis-${i}`}>Detail teknis</summary>
                    <div className="mt-1 font-mono text-[9px] text-slate-500 space-y-0.5">
                      <div>pengajuan: {x.pc_id || "—"}</div>
                      <div>item: {x.item_id || "—"}</div>
                      <div>kode: {x.reason || "—"}</div>
                    </div>
                  </details>
                </td>
                <td className="px-2 py-1">
                  {x.pc_id ? (
                    <Link
                      to={`/petty-cash?open=${x.pc_id}`}
                      className="text-[#FF5A1F] hover:underline text-[10px] uppercase tracking-wider whitespace-nowrap"
                      data-testid={`konflik-open-pc-${i}`}
                    >Lihat Pengajuan</Link>
                  ) : <span className="text-slate-400 italic text-[10px]">Tidak tersedia</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegacyBackfillSection({ rows, canBackfill, onOpen }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-amber-300 bg-amber-50" data-testid="legacy-backfill-section">
      <div className="px-3 py-1.5 text-[11px] font-semibold text-amber-900 border-b border-amber-200 flex items-center justify-between flex-wrap gap-2">
        <span className="flex items-center gap-1.5">
          <AlertTriangle size={12} /> Entry Legacy Tanpa Nominal Transfer
          <span className="text-amber-700 font-normal">· {rows.length} pengajuan</span>
        </span>
        <span className="text-[10px] text-amber-700 font-normal">
          {canBackfill
            ? "Pihak berwenang dapat melengkapi nominal transfer historis (alasan wajib)."
            : "Hanya pengurus FAT/BOD berwenang yang dapat melengkapi."}
        </span>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-amber-100/60">
            <tr>
              {["Minggu", "Proyek", "Tgl Transfer", "Fallback Total", "Pengajuan ID", "Aksi"].map((h) => (
                <th key={h} className="text-left px-2 py-1 border-b border-amber-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pc_id} className="border-b border-amber-100">
                <td className="px-2 py-1">{r.week_label || "—"}</td>
                <td className="px-2 py-1">{r.project_name || <span className="italic text-slate-500">Office/Legacy</span>}</td>
                <td className="px-2 py-1">{r.transfer_date || "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-800">{formatIDR(r.fallback_total || 0)}</td>
                <td className="px-2 py-1 font-mono text-[9px] text-slate-500">{(r.pc_id || "").slice(0, 10)}…</td>
                <td className="px-2 py-1">
                  {canBackfill ? (
                    <button
                      onClick={() => onOpen(r)}
                      className="px-2 py-0.5 border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 text-[10px] uppercase tracking-wider"
                      data-testid={`legacy-fill-${r.pc_id}`}
                    >Lengkapi Nominal</button>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">Butuh perm Konfirmasi Transfer</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegacyBackfillDialog({ row, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = parseFloat(String(amount).replace(/\./g, "").replace(/,/g, "."));
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Nominal transfer tidak valid (>= 0).");
      return;
    }
    if (!reason.trim()) {
      toast.error("Alasan wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/petty-cash/${row.pc_id}/legacy-transfer-amount`, {
        transfer_amount: amt,
        reason: reason.trim(),
      });
      toast.success("Nominal transfer historis tersimpan.");
      onDone();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan backfill legacy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="legacy-dialog">
      <div className="bg-white w-full max-w-md border border-slate-300 shadow-lg">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="font-display text-[13px] text-[#0B1D3A]">Lengkapi Nominal Transfer Historis</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-200" data-testid="legacy-dialog-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          <div className="bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
            <div><b>Pengajuan:</b> <span className="font-mono">{row.pc_id}</span></div>
            <div><b>Minggu:</b> {row.week_label || "—"} · <b>Proyek:</b> {row.project_name || "Office/Legacy"}</div>
            <div><b>Tanggal transfer:</b> {row.transfer_date || "—"}</div>
            <div><b>Fallback total pengajuan:</b> {formatIDR(row.fallback_total || 0)} <span className="text-amber-700">(hanya estimasi)</span></div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Nominal Transfer Aktual (Rp) <span className="text-rose-600">*</span>
            </label>
            <input
              type="number" min="0" step="1" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1 text-sm tabular-nums"
              placeholder="Mis. 100000"
              data-testid="legacy-amount-input"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">
              Rp 0 adalah nilai sah bila memang tidak ada transfer. Negatif ditolak.
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Alasan / Sumber Verifikasi <span className="text-rose-600">*</span>
            </label>
            <textarea
              rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1 text-xs"
              placeholder="Mis. Cek mutasi BCA 2026-01-15 Rp 100.000 ke Eunike T. · Lampiran: slip-xxxx.pdf"
              data-testid="legacy-reason-input"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">
              Alasan akan tercatat permanen di audit log petty cash.
            </div>
          </div>
        </div>
        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose} disabled={saving}
            className="px-3 py-1 text-xs border border-slate-300 hover:bg-slate-100"
            data-testid="legacy-cancel-btn"
          >Batal</button>
          <button
            onClick={submit} disabled={saving}
            className="px-3 py-1 text-xs bg-[#FF5A1F] text-white hover:bg-[#e04d16] disabled:opacity-50"
            data-testid="legacy-submit-btn"
          >{saving ? "Menyimpan…" : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}

function CadanganBreakdownTable({ rows, metric }) {
  const totalSisa = (rows || []).reduce((s, r) => s + (Number(r.sisa_cadangan) || 0), 0);
  const totalAlokasi = (rows || []).reduce((s, r) => s + (Number(r.alokasi_sah) || 0), 0);
  const totalUsage = (rows || []).reduce((s, r) => s + (Number(r.penggunaan_total) || 0), 0);
  const totalOverrun = (rows || []).reduce((s, r) => s + (Number(r.overrun) || 0), 0);
  return (
    <div className="border border-slate-200" data-testid="trx-cadangan-breakdown">
      <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-[#0B1D3A] border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <span>Cadangan Aktif — Rincian Pembentuk <span className="text-slate-400 font-normal">· {(rows || []).length} alokasi</span></span>
        <span className="text-[10px] text-slate-600 font-normal tabular-nums">
          Σ Alokasi {formatIDR(totalAlokasi)} · Σ Penggunaan {formatIDR(totalUsage)} · <b className="text-[#0B1D3A]">Σ Sisa {formatIDR(totalSisa)}</b>
          {totalOverrun > 0 && <span className="ml-1 text-rose-700"> · Σ Overrun {formatIDR(totalOverrun)}</span>}
        </span>
      </div>
      {metric && metric.has_overrun && (
        <div className="bg-rose-50 border-b border-rose-200 px-3 py-1.5 text-[10px] text-rose-700">
          Terdapat kelebihan penggunaan (overrun) — perlu diselesaikan oleh pengaju/FAT. Cadangan sisa diklamp ke 0 (tidak negatif).
        </div>
      )}
      <div className="overflow-auto max-h-[320px]">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-100">
            <tr>
              {["Pengajuan", "Proyek", "Alokasi Sah", "Transfer", "Carryover", "Penggunaan", "Sisa Cadangan", "Overrun", "Status", "Dibuat"].map((h) => (
                <th key={h} className="text-left px-2 py-1 border-b border-slate-200 sticky top-0 bg-slate-100">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <Fragment key={r.allocation_id}>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="px-2 py-1">
                    <div className="font-mono text-[10px] text-slate-600">{(r.pc_id || "").slice(0, 8)}…</div>
                  </td>
                  <td className="px-2 py-1">{r.project_name || <span className="italic text-slate-500">Office/Legacy</span>}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatIDR(r.alokasi_sah)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-600">{formatIDR(r.sumber_transfer)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-600">{formatIDR(r.sumber_carryover)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatIDR(r.penggunaan_total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold text-[#0B1D3A]">{formatIDR(r.sisa_cadangan)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-rose-700">{r.overrun > 0 ? formatIDR(r.overrun) : "—"}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${r.status === "active" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-100 border-slate-300 text-slate-600"}`}>
                      {r.status}{r.released_after_as_of ? "·snapshot" : ""}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-[10px] text-slate-500">{r.created_at ? String(r.created_at).slice(0, 10) : "—"}</td>
                </tr>
                {(r.usages || []).length > 0 && (
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <td colSpan={10} className="px-4 py-1 text-[10px] text-slate-600">
                      <b>Rincian penggunaan ({r.usages.length}):</b>
                      <ul className="ml-3 mt-0.5 space-y-0.5">
                        {r.usages.map((u) => (
                          <li key={u.usage_id}>
                            <span className="font-mono text-[9px] text-slate-500">{(u.item_id || "").slice(0, 8)}…</span>
                            {" · "}<span className="tabular-nums">{formatIDR(u.amount)}</span>
                            {u.previous_amount > 0 && u.previous_amount !== u.amount && (
                              <span className="text-amber-700"> (sebelumnya {formatIDR(u.previous_amount)}, v{u.effective_version})</span>
                            )}
                            {" · "}<span className="text-slate-500">{u.kind}</span>
                            {" · "}<span className="text-slate-400 italic">{String(u.updated_at || u.created_at || "").slice(0, 16)}</span>
                            {u.actor_name && <span className="text-slate-500"> · oleh {u.actor_name}</span>}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {(rows || []).length === 0 && (
              <tr><td colSpan={10} className="text-center p-3 text-slate-500">Tidak ada alokasi aktif pada tanggal acuan.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrxTable({ title, testid, rows, cols }) {
  return (
    <div className="border border-slate-200" data-testid={testid}>
      <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-[#0B1D3A] border-b border-slate-200">
        {title} <span className="text-slate-400 font-normal">· {rows.length} baris</span>
      </div>
      <div className="overflow-auto max-h-[280px]">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-100">
            <tr>
              {cols.map((c) => (
                <th key={c.k} className="text-left px-2 py-1 border-b border-slate-200 sticky top-0 bg-slate-100">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-slate-100">
                {cols.map((c) => {
                  const v = row[c.k];
                  if (c.money) {
                    if (v === null || v === undefined) {
                      return <td key={c.k} className="px-2 py-1 text-right text-amber-700 italic">
                        {c.allowNull || "—"}
                      </td>;
                    }
                    return <td key={c.k} className="px-2 py-1 text-right tabular-nums">{formatIDR(v)}</td>;
                  }
                  return <td key={c.k} className="px-2 py-1">{v ?? "—"}</td>;
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={cols.length} className="text-center p-3 text-slate-500">(kosong)</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
